/**
 * Raw design tokens. Everything visual traces back to a value in this file, so
 * a change here propagates rather than needing to be hunted down in components.
 *
 * Accent colours carry the whole product concept — good / bad / ideal — so the
 * neutrals are deliberately close to achromatic and let the accents do the work.
 */

/** Neutral ramp. Near-black and off-white anchors per spec §9.1. */
export const neutral = {
  0: '#FFFFFF',
  25: '#FAFAF8',
  50: '#F4F4F1',
  100: '#EAEAE6',
  200: '#DCDCD6',
  300: '#C4C4BD',
  400: '#9A9A93',
  500: '#757570',
  600: '#5C616B',
  700: '#3C4048',
  800: '#252830',
  850: '#1D2027',
  900: '#161920',
  950: '#0F1115',
} as const;

/**
 * The three registers.
 *
 * `main` is the identity colour — the 4px accent bar, borders, icon fills. It
 * must clear WCAG AA for non-text UI (3:1) against the surface behind it.
 *
 * `text` is the colour accent-coloured *words* use, and must clear AA for text
 * (4.5:1). In light mode that is necessarily darker than `main`; in dark mode
 * the same value clears both, so they coincide.
 *
 * `solid` is the fill behind a label (contained buttons, filled badges), with
 * `contrastText` as that label's colour.
 *
 * The light-mode `ideal.main` is #B08420 rather than the #C89B2C the brief
 * suggested: that value measures 2.46:1 against the page background, which
 * fails even the 3:1 non-text threshold the same brief requires. See
 * docs/DECISIONS.md and theme/contrast.test.ts.
 */
export const accents = {
  light: {
    good: {
      main: '#2E9E5B',
      text: '#1B6E3D',
      solid: '#1B6E3D',
      contrastText: '#FFFFFF',
      soft: 'rgba(46, 158, 91, 0.10)',
      softer: 'rgba(46, 158, 91, 0.05)',
      border: 'rgba(46, 158, 91, 0.38)',
    },
    bad: {
      main: '#D64545',
      text: '#B32828',
      solid: '#B32828',
      contrastText: '#FFFFFF',
      soft: 'rgba(214, 69, 69, 0.10)',
      softer: 'rgba(214, 69, 69, 0.05)',
      border: 'rgba(214, 69, 69, 0.38)',
    },
    ideal: {
      main: '#B08420',
      text: '#7A5C0F',
      solid: '#7A5C0F',
      contrastText: '#FFFFFF',
      soft: 'rgba(176, 132, 32, 0.12)',
      softer: 'rgba(176, 132, 32, 0.06)',
      border: 'rgba(176, 132, 32, 0.42)',
    },
  },
  dark: {
    good: {
      main: '#4ADE80',
      text: '#4ADE80',
      solid: '#4ADE80',
      contrastText: '#0F1115',
      soft: 'rgba(74, 222, 128, 0.14)',
      softer: 'rgba(74, 222, 128, 0.07)',
      border: 'rgba(74, 222, 128, 0.34)',
    },
    bad: {
      main: '#F87171',
      text: '#F87171',
      solid: '#F87171',
      contrastText: '#0F1115',
      soft: 'rgba(248, 113, 113, 0.14)',
      softer: 'rgba(248, 113, 113, 0.07)',
      border: 'rgba(248, 113, 113, 0.34)',
    },
    ideal: {
      main: '#FBBF24',
      text: '#FBBF24',
      solid: '#FBBF24',
      contrastText: '#0F1115',
      soft: 'rgba(251, 191, 36, 0.14)',
      softer: 'rgba(251, 191, 36, 0.07)',
      border: 'rgba(251, 191, 36, 0.34)',
    },
  },
} as const;

/**
 * The one colour in the app that is not neutral or a register.
 *
 * A mention has to read as a link inside a paragraph of someone else's writing,
 * and none of good / bad / ideal can do that job — a name in green would look
 * like a verdict on the person. Blue is the convention for "this is a link to
 * someone", which is exactly the meaning wanted, and it stays clear of the
 * three registers the whole product rests on.
 *
 * Both values clear WCAG AA for text on both surfaces of their mode; the
 * contrast suite proves it rather than trusting the eye.
 */
export const mention = {
  light: '#1D4ED8',
  dark: '#93B4FF',
} as const;

/**
 * Business surfaces — an official response, a verified badge, a business chip.
 *
 * A desaturated blue-grey, deliberately not green, red or gold: a brand's own
 * words must never wear one of the three registers, or a feed of official
 * replies starts reading like a verdict the app has passed. Quiet on purpose.
 */
export const business = {
  light: '#42566B',
  dark: '#A7BACB',
} as const;

/** Radii — 12px cards, 10px inputs, pill chips (spec §9.1). */
export const radius = {
  sm: 8,
  input: 10,
  card: 12,
  lg: 16,
  pill: 999,
} as const;

/**
 * Soft, low-contrast elevation. Nothing like MUI's default 25-step stack —
 * in dark mode a 1px border reads better than a shadow, so the dark values are
 * deliberately almost invisible and borders carry the separation instead.
 */
export const shadows = {
  light: {
    none: 'none',
    xs: '0 1px 2px rgba(16, 18, 22, 0.04)',
    sm: '0 1px 3px rgba(16, 18, 22, 0.06), 0 1px 2px rgba(16, 18, 22, 0.04)',
    md: '0 4px 12px rgba(16, 18, 22, 0.07), 0 1px 3px rgba(16, 18, 22, 0.05)',
    lg: '0 12px 32px rgba(16, 18, 22, 0.10), 0 2px 8px rgba(16, 18, 22, 0.05)',
  },
  dark: {
    none: 'none',
    xs: '0 1px 2px rgba(0, 0, 0, 0.30)',
    sm: '0 1px 3px rgba(0, 0, 0, 0.40)',
    md: '0 4px 14px rgba(0, 0, 0, 0.50)',
    lg: '0 16px 40px rgba(0, 0, 0, 0.60)',
  },
} as const;

/** 8px base (spec §9.1). */
export const SPACING_UNIT = 8;

/** Layout widths — three columns on desktop, per spec §9.3. */
export const layout = {
  /**
   * The width every column is measured against — the header and the page share
   * it, which is what makes the logo line up with the nav rail and the account
   * menu line up with the sidebar.
   *
   * 1280 is chosen so the three columns add up exactly: 240 rail + 24 gap +
   * 644 feed + 24 gap + 300 sidebar + 48 padding. The feed column lands just
   * under its 680px reading cap without leaving a gap to fill.
   */
  pageMaxWidth: 1280,
  navRailWidth: 240,
  navRailCollapsedWidth: 72,
  feedMaxWidth: 680,
  /**
   * How wide the centre column is allowed to grow on pages that have no
   * sidebar. Without a cap a settings form would stretch to 970px and its lines
   * would be twice as long as the feed's.
   */
  contentMaxWidth: 760,
  sidebarWidth: 300,
  appBarHeight: 64,
  /**
   * Tall enough for an icon above a label, with room to spare.
   *
   * 60 was not: MUI stacks a 20px icon over a 20px label with padding between,
   * and the bar was told `height` rather than a minimum — so the labels
   * overflowed its box, and since the box sits at `bottom: 0` the overflow went
   * off the bottom of the screen. The labels were cut in half on a phone.
   */
  bottomNavHeight: 68,
  gutter: 24,
} as const;

/** Motion — subtle, 150–200ms, and always overridable by prefers-reduced-motion. */
export const motion = {
  fast: 150,
  base: 180,
  slow: 240,
  easing: 'cubic-bezier(0.2, 0, 0.2, 1)',
  spring: 'cubic-bezier(0.34, 1.56, 0.64, 1)',
} as const;

export type AccentName = 'good' | 'bad' | 'ideal';
export type ColorMode = 'light' | 'dark';

/**
 * Hides an element from the screen while leaving it in the accessibility tree.
 *
 * For labels that a sighted reader does not need — a lone select beside a page
 * heading reads for itself — but which a screen reader still has to announce.
 * The same declaration MUI ships in `@mui/utils`, written out here because that
 * package is not a direct dependency of this project.
 *
 * Every length carries its unit, and must. This is used through `sx`, where a
 * unitless number is not pixels: `width: 1` means 100%, and `margin: -1` means
 * -8px, one step of the spacing scale. So the element that was meant to be one
 * pixel square was the full width of its container and eight pixels wider on
 * each side — invisible, because of `clip`, but still taking up room. Three of
 * them on the dashboard put the page nine pixels past the phone, and the fixed
 * bottom bar spans the page rather than the screen, so its right-hand end and
 * the labels went over the edge.
 */
export const visuallyHidden = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  padding: 0,
  margin: '-1px',
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
  border: 0,
} as const;
